'use client'

import { useCallback, useState, useEffect } from 'react';
import Image from 'next/image';
import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaCarouselType } from 'embla-carousel';
import { motion, Variants } from 'framer-motion';

import { ParagraphSection } from '@/types/drupal';
import { Icon } from '../ui/Icon';

const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { 
      duration: 0.8,
      ease: [0.21, 0.47, 0.32, 0.98] 
    } 
  }
};

const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

export default function Industries({ data }: { data: ParagraphSection }) {
  const bgImage = data.image?.mediaImage;
  const industries = data.industries || [];

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    loop: false,
    slidesToScroll: 1,
  });

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateScrollButtons = useCallback((api: EmblaCarouselType) => {
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    queueMicrotask(() => updateScrollButtons(emblaApi));
    emblaApi.on('select', updateScrollButtons);
    emblaApi.on('reInit', updateScrollButtons);

    return () => {
      emblaApi.off('select', updateScrollButtons);
      emblaApi.off('reInit', updateScrollButtons);
    };
  }, [emblaApi, updateScrollButtons]);

  return (
    <section
      id="industries"
      className="Industries relative my-[30px] lg:my-[50px] lg:py-[50px] bg-[#01060F] overflow-hidden"
      style={{ '--bg-industries-image': bgImage ? `url("${bgImage.url}")` : 'none' } as React.CSSProperties}
      aria-label="Industries"
    >
      <div className="lg:border-t lg:border-b lg:border-[#D9D9D9] relative">
        <div
          aria-hidden="true"
          className="absolute inset-0 z-0 hidden lg:block pointer-events-none select-none bg-[image:var(--bg-industries-image)] bg-size-[55%_auto] bg-position-[80%_center] bg-no-repeat opacity-100"
        />

        <div className="site-container relative z-10">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="section-container py-[60px] lg:py-[60px] flex flex-col items-center lg:items-start text-center lg:text-left"
          >
             {/* Title */}
            <motion.div
              variants={fadeInUp}
              className="Industries__heading max-w-[290px] lg:max-w-[430px]"
              dangerouslySetInnerHTML={{ __html: data.title?.processed || '' }}
            />

            {/* Body */}
            <motion.div
              variants={fadeInUp}
              className="Industries__body mt-4 lg:mt-[30px] max-w-[340px] lg:max-w-[380px] text-white/70"
              dangerouslySetInnerHTML={{ __html: data.body?.processed || '' }}
            />

            <motion.p 
              variants={fadeInUp}
              className="max-w-[340px] lg:max-w-[auto] mt-[30px] lg:mt-20 text-[17px] leading-[1.1] lg:text-[22px] text-[#FCFED1] font-semibold"
            >
              Industries we serve:
            </motion.p>

            {/* Slider Container */}
            <motion.div
              variants={fadeInUp}
              className="relative mt-[50px] lg:mt-[30px] flex items-center gap-4 max-w-[calc(100%_+_20px)] lg:max-w-[590px]"
              role="region"
              aria-roledescription="carousel"
              aria-label="Industries we serve"
            >
              {/* Left Arrow */}
              <button
                onClick={() => emblaApi?.scrollPrev()}
                disabled={!canScrollPrev}
                aria-label="Previous industries"
                className={
                  `relative left-0 transition-all duration-200
                  ${canScrollPrev ? "opacity-100 hover:scale-110" : "opacity-20 cursor-not-allowed"}
                `}
              >
                <Icon name="ChevronLeft" size={26} color="#D1E0FA" />
              </button>

              {/* Slider Viewport */}
              <div className="overflow-hidden w-full" ref={emblaRef}>
                <motion.div variants={staggerContainer} className="flex gap-2">
                  {industries.map((item, index) => (
                    <motion.div
                      key={index} 
                      role="group"
                      aria-roledescription="slide"
                      aria-label={item.name}
                      variants={fadeInUp}
                      className="flex-[0_0_calc(100%_/_2)] md:flex-[0_0_calc(100%_/_3)] lg:flex-[0_0_25%] min-w-0 flex flex-col items-center gap-2"
                    >
                      <div className="relative w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center">
                        <Image
                          src={item.icon?.mediaImage?.url}
                          alt=""
                          aria-hidden="true"
                          fill
                          className="object-contain opacity-80"
                          sizes="(max-width: 1024px) 40px, 48px"
                        />
                      </div>
                      <p className="text-sm lg:text-base leading-[1.1] text-white text-center whitespace-nowrap">
                        {item.name}
                      </p>
                    </motion.div>
                  ))}
                </motion.div>
              </div>

              {/* Right Arrow */}
              <button
                onClick={() => emblaApi?.scrollNext()}
                disabled={!canScrollNext}
                aria-label="Next industries"
                className={`
                  relative lg:right-0 transition-all duration-200
                  ${canScrollNext ? "opacity-100 hover:scale-110" : "opacity-20 cursor-not-allowed"}
                `}
              >
                <Icon name="ChevronRight" size={32} color="#D1E0FA" />
              </button>

            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
